import type { NewChartSettingFormState } from './chartSectionList.interfaces';
import { isReadCategoryMismatch } from './chartReadRange.utils';
import type { ChartReadEnd, ChartReadStart, ChartTargetType, ChartType } from 'interfaces/chartOfSection.interface';

interface AddSettingValidationResult {
  readCategoryMismatch: boolean;
  isSaveDisabled: boolean;
}

interface AddSettingValidationOptions {
  chartTypeIds: string[];
  chartUnitIds: string[];
  managerIds: string[];
  targetTypeIds: string[];
  readStartIds: string[];
  readEndIds: string[];
  themeIds: string[];
}

const isNonEmpty = ( value?: string | null ): boolean => {
  return ( value ?? '' ).trim() !== '';
};

const isOptionSelected = ( selected: string, optionIds: string[] ): boolean => {
  return optionIds.includes( selected );
};

const isPositiveNumber = ( value?: string ): boolean => {
  if ( !isNonEmpty( value ) ) {
    return false;
  }
  const parsed = Number( value );
  return Number.isFinite( parsed ) && parsed >= 1;
};

export const getAddSettingValidation = (
  newSetting: NewChartSettingFormState,
  hasTargetValue: boolean,
  options: AddSettingValidationOptions,
): AddSettingValidationResult => {
  const chartType = newSetting.type as ChartType;
  const isAutoIncrementGauge = chartType === 'auto_increment_gauge';
  const isProgressGauge = chartType === 'progress_gauge';
  const needsTargetFields = hasTargetValue && !isProgressGauge && !isAutoIncrementGauge;

  const readCategoryMismatch = isReadCategoryMismatch( newSetting.readStart, newSetting.readEnd );
  const shouldCreateTicket = newSetting.createTicketOnTargetMiss === '1';
  const hourFormatIsValid = /^(?:[01]\d|2[0-3]):[0-5]\d$/.test( newSetting.ticketCreationHour?.trim() ?? '' );
  const hasTicketTheme = isNonEmpty( newSetting.alertThemeId ) && isOptionSelected( newSetting.alertThemeId, options.themeIds );

  const requireHour = shouldCreateTicket && !isAutoIncrementGauge;
  const ticketValidationFails = shouldCreateTicket && ( ( requireHour && !hourFormatIsValid ) || !hasTicketTheme );

  const hasValidType = isOptionSelected( chartType, options.chartTypeIds );
  const hasValidChartUnit = isOptionSelected( newSetting.chartUnitId, options.chartUnitIds );
  const hasValidManager = isOptionSelected( newSetting.managerId, options.managerIds );
  const hasValidReadStart = isOptionSelected( newSetting.readStart as ChartReadStart, options.readStartIds );
  const hasValidReadEnd = isOptionSelected( newSetting.readEnd as ChartReadEnd, options.readEndIds );

  const hasValidTargetType = isOptionSelected( newSetting.targetType as ChartTargetType, options.targetTypeIds );
  const hasValidTargetName = isNonEmpty( newSetting.targetName );
  const hasValidTargetValue = isNonEmpty( newSetting.targetValue );
  const hasValidTargetColor = chartType === 'custom_gauge' ? true : isNonEmpty( newSetting.targetColor );

  const autoIncrementFieldsInvalid = isAutoIncrementGauge
    && ( !isPositiveNumber( newSetting.incrementValue ) || !isPositiveNumber( newSetting.incrementFrequencyDays ) );

  const isSaveDisabled =
    !isNonEmpty( newSetting.title )
    || !isNonEmpty( newSetting.dataName )
    || !hasValidType
    || !hasValidChartUnit
    || !hasValidManager
    || !hasValidReadStart
    || !hasValidReadEnd
    || ( needsTargetFields && ( !hasValidTargetName || !hasValidTargetValue || !hasValidTargetType || !hasValidTargetColor ) )
    || autoIncrementFieldsInvalid
    || ticketValidationFails
    || readCategoryMismatch;

  return {
    readCategoryMismatch,
    isSaveDisabled,
  };
};
